import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  Formik,
  Form,
  Field,
  ErrorMessage,
  FieldProps,
} from 'formik';

import TextField from 'hellospa/foundation/control/text-field';
import Toggle from 'hellospa/components/toggle';
import HelloModal from 'common/components/hello-modal';
import { trackHeapCustomEvent } from 'common/utils/heap';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import messages from './intl';
import styles from './index.scss';

const SignerInfoModal: types.TriggerComponent = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    alwaysSendMeACopy,
    alwaysSendOthersACopy,
  } = useSelector(selectors.getFlags);

  const name = useSelector(selectors.getUser).name;
  // split name into first name & last name because it comes as full name from the backend.
  const fullName = name ? name.split(' ') : [];
  const firstName = fullName[0] || '';
  const lastName = fullName[1] || '';

  const schema = Yup.object<types.UpdateAccountParams>({
    firstName: Yup.string().required(intl.formatMessage(messages.firstNameError)),
    lastName: Yup.string().required(intl.formatMessage(messages.lastNameError)),
    alwaysSendMeACopy: Yup.boolean(),
    alwaysSendOthersACopy: Yup.boolean(),
  });

  const initialValues: types.UpdateAccountParams = {
    firstName,
    lastName,
    alwaysSendMeACopy: Boolean(alwaysSendMeACopy),
    alwaysSendOthersACopy: Boolean(alwaysSendOthersACopy),
  };

  // submit the info modal which should trigger the parent (review) form
  // using the onSubmit prop being passed into this component
  const sendSignature = React.useCallback((
    values: types.UpdateAccountParams,
  ) => {
    // not waiting so that the request is sent regardless of a server error
    // mimicking the existing prep & send functionality
    dispatch(actions.updateAccount(values));
  }, [dispatch]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        sendSignature(values);
      }}
      validateOnChange={false}
      validationSchema={schema}
      // eslint-disable-next-line react/no-children-prop
      children={({ submitForm, handleChange }) => (
        <HelloModal
          contentLabel={intl.formatMessage(messages.contentTitle)}
          width="540px"
          scrollable={false}
          shouldCloseOnEsc={false}
          isOpen={true}
          onRequestClose={undefined}
          showCloseIcon={false}
          buttons={[
            {
              type: 'primary',
              'data-qa-ref': 'signer-info-modal-submit',
              text: intl.formatMessage(messages.submitButton),
              onClick: async () => {
                trackHeapCustomEvent('sign_or_send_for_signature_confirm');
                await submitForm();
              },
            },
          ]}
        >
          <Form
            onChange={(e) => {
              // Prevents parent's onChange from triggering,
              // which causes loss of field focus
              e.stopPropagation();
              return handleChange;
            }}
          >
            <div className={styles.profileContainer}>
              <div className={styles.infoText}>
                <FormattedMessage
                  id=""
                  description="info text on the modal asking to enter full name"
                  defaultMessage="Add your name so recipients know who is sending the documents" />
              </div>
              <div className={styles.nameContainer}>
                <div className={styles.firstName}>
                  <label htmlFor="firstName">
                    <FormattedMessage
                      id=""
                      description="label for first name"
                      defaultMessage="First name" />
                  </label>
                  <Field
                    name="firstName"
                  >
                    {({ field }: FieldProps) => <TextField
                      id="firstName"
                      role="firstName"
                      data-qa-ref="signer-info-modal-firstname"
                      {...field} />}
                  </Field>
                  <ErrorMessage
                    name="firstName"
                    component="span"
                    className={styles.error} />
                </div>
                <div className={styles.lastName}>
                  <label htmlFor="lastName">
                    <FormattedMessage
                      id=""
                      description="label for last name"
                      defaultMessage="Last name" />
                  </label>
                  <Field
                    name="lastName"
                  >
                    {({ field }: FieldProps) => <TextField
                      id="lastName"
                      role="lastName"
                      data-qa-ref="signer-info-modal-lastname"
                      {...field} />}
                  </Field>
                  <ErrorMessage
                    name="lastName"
                    component="span"
                    className={styles.error} />
                </div>
              </div>
              <div className={styles.infoText}>
                <FormattedMessage
                  id=""
                  description="info text on the modal asking to select CC options"
                  defaultMessage="Decide who should receive documents as an email attachment" />
              </div>
              <div className={styles.ccContainer}>
                <div className={styles.ccMe}>
                  <Field name="alwaysSendMeACopy">
                    {({ field, form }: FieldProps) => (
                      <Toggle
                        id="ccMe"
                        checked={field.value}
                        data-qa-ref="signer-info-modal-cc-me"
                        onChange={() => {
                          form.setFieldValue(field.name, !field.value);
                        }}
                        indeterminate={false} />
                    )}
                  </Field>
                  <label htmlFor="ccMe">
                    <FormattedMessage
                      id=""
                      description="text for toggling always send copy for me or others for signer info modal"
                      defaultMessage="Always send <strong>me</strong> a copy"
                      values={{
                        // eslint-disable-next-line react/display-name
                        strong: (...chunks: any[]) => <strong key="ccMe-strong">{chunks}</strong>,
                      }} />
                  </label>
                </div>
                <div className={styles.ccOthers}>
                  <Field name="alwaysSendOthersACopy">
                    {({ field, form }: FieldProps) => (
                      <Toggle
                        id="ccOthers"
                        checked={field.value}
                        data-qa-ref="signer-info-modal-cc-others"
                        onChange={() => {
                          form.setFieldValue(field.name, !field.value);
                        }}
                        indeterminate={false} />
                    )}
                  </Field>
                  <label htmlFor="ccOthers">
                    <FormattedMessage
                      id=""
                      description="text for toggling always send copy for me or others for signer info modal"
                      defaultMessage="Always send <strong>other recipients and anyone CC'ed</strong> a copy"
                      values={{
                        // eslint-disable-next-line react/display-name
                        strong: (...chunks: any[]) => <strong key="ccOthers-strong">{chunks}</strong>,
                      }} />
                  </label>
                </div>
              </div>
              <p className={styles.infoSubText}>
                <FormattedMessage
                  id=""
                  description="info text for changing options in settings later"
                  defaultMessage="You can always change these options later in your settings" />
              </p>
            </div>
          </Form>
        </HelloModal>
      )} />
  );
};

SignerInfoModal.needsTrigger = (state) => {
  const isEmbedded = selectors.isEmbedded(state);
  const isHelloFax = selectors.isHelloFax(state);
  const isSignerInfoRequired = selectors.isSignerInfoRequired(state);

  return isHelloFax && !isEmbedded && isSignerInfoRequired;
};

export default SignerInfoModal;
